'use client'

import styles from './GamepadsModal.module.css'
import { useTranslations } from 'next-intl'
import Button, { ButtonKind, ButtonSize } from '@/components/UI/Buttons/Button/Button'
import IconButton, { IconButtonKind, IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import IconCross from '@/assets/img/Icon_Cross.svg'
import { FEATURES, MAX_CONNECTED_PADS } from '@/utils/constants'
import GamepadDevCard from './components/GamepadDevCard'
import VPadDevCard from './components/VPadDevCard'
import { useModalStore } from '@/stores/useModalStore'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import VPadPairingModal from '../VPadPairingModal/VPadPairingModal'
import { usePadsStore } from '@/stores/usePadsStore'
import { VPadStage } from '@/interfaces/virtualPad_types'
import { useEffect } from 'react'

export default function GamepadsModal () {
  const t = useTranslations('Modals.GamepadsManager')
  const openModal = useModalStore(state => state.openModal)
  const closeModal = useModalStore(state => state.closeModal)
  const physicalPadsRecord = usePadsStore((state) => state.physicalPads)
  const vpadsRecord = usePadsStore((state) => state.vpads)

  const physicalPads = Object.values(physicalPadsRecord).sort((a, b) => a.index - b.index)
  const vpads = Object.values(vpadsRecord)
  const availableVPads = vpads.filter((vPad) => vPad.stage === VPadStage.CONNECTED || vPad.stage === VPadStage.SUSPENDED)
  const hasMaxConnectedPads = availableVPads.length >= MAX_CONNECTED_PADS

  /**
   * Show the gamepads modal
   */
  function handlePairing () {
    openModal({
      type: ModalTypes.LATERAL,
      content: <VPadPairingModal />
    })
  }

  useEffect(() => {
    if (physicalPads.length > 0 || availableVPads.length > 0) return

    openModal({
      type: ModalTypes.LATERAL,
      content: <VPadPairingModal />
    })
  }, [physicalPads.length, availableVPads.length, openModal])

  return (
    <div className={styles.gamepadsModal} >
      <div className={styles.closeBtnWrapper}>
        <IconButton
          icon={IconCross}
          size={IconButtonSize.M}
          kind={IconButtonKind.EMPTY}
          onClick={closeModal}
        />
      </div>

      <div className={styles.mainWrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.headerWrapper}>
            <h1 className={`title2`}>{t('title')}</h1>
            <h2 className={`bodyTextLarge`}>{t('subtitle')}</h2>
          </div>

          <div className={styles.devicesWrapper}>
            {physicalPads.map((gamepad) => (
              <GamepadDevCard 
                key={gamepad.index}
                pad={gamepad}
              />
            ))}
            
            {vpads.map((vPad) => (
              <VPadDevCard
                key={vPad.connectionId}
                vPad={vPad}
              />
            ))}
          </div>

          <div className={styles.optionsWrapper}>
            {hasMaxConnectedPads &&
            <p className={`bodyTextSmall ${styles.maxControlsTxt}`}>{t('maxControlsInfo')}</p>
            }
            <Button
              kind={ButtonKind.PRIMARY}
              size={ButtonSize.MEDIUM}
              disabled={hasMaxConnectedPads}
              onClick={handlePairing}
            >
              {t('pairBtn')}
            </Button>
          </div>
        </div>

        {FEATURES.virtualPadsBeta && <p className={`bodyTextSmall ${styles.betaText}`}>{t('betaInfo')}</p>}
      </div>
    </div>
  )
}